import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Text,
  Textarea,
  VStack,
  HStack,
  Icon,
  Badge,
  SimpleGrid,
  Spinner,
  Center,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  useColorMode,
} from '@chakra-ui/react'
import {
  AlertTriangle,
  Check,
  Copy,
  Trash2,
  Clock,
  Eye,
  Flame,
  Home,
  Pencil,
  RefreshCw,
  FileCode,
} from 'lucide-react'
import { formatDate } from '@core/utils/format'
import { useShareManagement } from './useShareManagement'

export function ShareManagePage() {
  const {
    state,
    shareData,
    error,
    navigate,
    // Revoke
    isRevoking,
    isRevokeOpen,
    onRevokeOpen,
    onRevokeClose,
    handleRevoke,
    // Edit
    editState,
    editPasscode,
    setEditPasscode,
    editContent,
    setEditContent,
    contentDecrypted,
    isEditOpen,
    handleOpenEdit,
    handleCloseEdit,
    handleDecryptForEdit,
    handleSaveEdit,
    // Clipboard
    hasCopied,
    copyToClipboard,
    getRawUrl,
    copyRawLinkToClipboard,
    getStatus,
  } = useShareManagement()

  const { colorMode } = useColorMode()

  if (state === 'loading') {
    return (
      <Center minH="400px">
        <Spinner size="xl" color="brand.500" />
      </Center>
    )
  }

  if (state === 'error') {
    return (
      <Card maxW="md" mx="auto">
        <CardBody>
          <VStack spacing={4} textAlign="center">
            <Icon as={AlertTriangle} boxSize={12} color="red.500" />
            <Heading size="md" fontFamily="heading">Invalid Token</Heading>
            <Text color="stone.500">{error}</Text>
            <Button variant="outline" leftIcon={<Home size={16} />} onClick={() => navigate('/')}>
              Go Home
            </Button>
          </VStack>
        </CardBody>
      </Card>
    )
  }

  if (state === 'revoked') {
    return (
      <Card maxW="md" mx="auto">
        <CardBody>
          <VStack spacing={4} textAlign="center">
            <Flex
              w={16}
              h={16}
              bg="green.100"
              borderRadius="full"
              align="center"
              justify="center"
            >
              <Icon as={Check} boxSize={8} color="green.600" />
            </Flex>
            <Heading size="md" fontFamily="heading">Share Revoked</Heading>
            <Text color="stone.500">
              This share has been permanently revoked. Anyone with the link will
              see a "Share not found" message.
            </Text>
            <Button leftIcon={<Home size={16} />} onClick={() => navigate('/')}>
              Create New Share
            </Button>
          </VStack>
        </CardBody>
      </Card>
    )
  }

  const status = getStatus()

  return (
    <VStack spacing={6} maxW="2xl" mx="auto">
      <Card w="full">
        <CardHeader>
          <HStack justify="space-between">
            <Heading size="md">Share Status</Heading>
            {status && (
              <Badge colorScheme={status.colorScheme} fontSize="sm" px={3} py={1} borderRadius="full">
                {status.label}
              </Badge>
            )}
          </HStack>
          <Text fontSize="sm" color="stone.500" mt={1}>
            View and manage your shared content
          </Text>
        </CardHeader>
        <CardBody pt={0}>
          {shareData && (
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>Share Link</Text>
                <InputGroup>
                  <Input
                    value={shareData.shareUrl}
                    readOnly
                    fontFamily="mono"
                    fontSize="sm"
                    bg={colorMode === 'dark' ? 'stone.700' : 'stone.50'}
                    pr={12}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label="Copy link"
                      icon={hasCopied('share') ? <Check size={16} color="green" /> : <Copy size={16} />}
                      variant="ghost"
                      size="sm"
                      onClick={copyToClipboard}
                    />
                  </InputRightElement>
                </InputGroup>
              </Box>

              <Box>
                <HStack mb={2}>
                  <Icon as={FileCode} boxSize={4} color="stone.500" />
                  <Text fontSize="sm" fontWeight="medium">Raw Link</Text>
                </HStack>
                <Text fontSize="xs" color="stone.500" mb={2}>
                  {shareData.isPublic
                    ? 'Direct access to raw markdown without headers'
                    : shareData.shareUrl.includes('#')
                    ? 'Raw markdown with passcode in URL'
                    : 'Replace YOUR_PASSCODE with the actual passcode'}
                </Text>
                <InputGroup>
                  <Input
                    value={getRawUrl()}
                    readOnly
                    fontFamily="mono"
                    fontSize="sm"
                    bg={colorMode === 'dark' ? 'stone.700' : 'stone.50'}
                    pr={12}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label="Copy raw link"
                      icon={hasCopied('raw') ? <Check size={16} color="green" /> : <Copy size={16} />}
                      variant="ghost"
                      size="sm"
                      onClick={copyRawLinkToClipboard}
                    />
                  </InputRightElement>
                </InputGroup>
              </Box>

              <SimpleGrid columns={2} spacing={4} pt={4}>
                <Box>
                  <HStack fontSize="sm" color="stone.500" mb={1}>
                    <Icon as={Clock} boxSize={4} />
                    <Text>Created</Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="medium">
                    {formatDate(shareData.createdAt)}
                  </Text>
                </Box>
                <Box>
                  <HStack fontSize="sm" color="stone.500" mb={1}>
                    <Icon as={Clock} boxSize={4} />
                    <Text>Expires</Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="medium">
                    {formatDate(shareData.expiresAt, true)}
                  </Text>
                </Box>
                <Box>
                  <HStack fontSize="sm" color="stone.500" mb={1}>
                    <Icon as={Eye} boxSize={4} />
                    <Text>Views</Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="medium">{shareData.viewCount}</Text>
                </Box>
                <Box>
                  <HStack fontSize="sm" color="stone.500" mb={1}>
                    <Icon as={Flame} boxSize={4} />
                    <Text>Burn after read</Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="medium">
                    {shareData.burnAfterRead ? 'Yes' : 'No'}
                  </Text>
                </Box>
                <Box>
                  <HStack fontSize="sm" color="stone.500" mb={1}>
                    <Icon as={Pencil} boxSize={4} />
                    <Text>Modifiable</Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="medium">
                    {shareData.isModifiable ? 'Yes' : 'No'}
                  </Text>
                </Box>
                {shareData.updatedAt && (
                  <Box>
                    <HStack fontSize="sm" color="stone.500" mb={1}>
                      <Icon as={RefreshCw} boxSize={4} />
                      <Text>Last Updated</Text>
                    </HStack>
                    <Text fontSize="sm" fontWeight="medium">
                      {formatDate(shareData.updatedAt)}
                    </Text>
                  </Box>
                )}
              </SimpleGrid>

              {!shareData.revokedAt && !shareData.consumedAt && shareData.expiresAt > Date.now() && (
                <Box pt={4} borderTopWidth="1px">
                  <VStack spacing={3}>
                    {shareData.isModifiable && (
                      <Button
                        colorScheme="blue"
                        w="full"
                        leftIcon={<Pencil size={16} />}
                        onClick={handleOpenEdit}
                      >
                        Edit Content
                      </Button>
                    )}
                    <Button
                      colorScheme="red"
                      variant={shareData.isModifiable ? 'outline' : 'solid'}
                      w="full"
                      leftIcon={<Trash2 size={16} />}
                      onClick={onRevokeOpen}
                    >
                      Revoke Share
                    </Button>
                  </VStack>
                </Box>
              )}
            </VStack>
          )}
        </CardBody>
      </Card>

      {/* Revoke Modal */}
      <Modal isOpen={isRevokeOpen} onClose={onRevokeClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Revoke Share</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to revoke this share? This action
              cannot be undone. Anyone with the link will no longer be
              able to access the content.
            </Text>
          </ModalBody>
          <ModalFooter justifyContent="center">
            <Button variant="ghost" mr={3} onClick={onRevokeClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleRevoke}
              isLoading={isRevoking}
              loadingText="Revoking..."
            >
              Yes, Revoke
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} onClose={handleCloseEdit} size="xl">
        <ModalOverlay />
        <ModalContent maxW="800px">
          <ModalHeader>Edit Share Content</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {!contentDecrypted ? (
              <VStack spacing={4} align="stretch">
                <Text color="stone.600">
                  Enter your passcode to decrypt and edit the content.
                </Text>
                <FormControl>
                  <FormLabel>Passcode</FormLabel>
                  <Input
                    type="password"
                    value={editPasscode}
                    onChange={(e) => setEditPasscode(e.target.value)}
                    placeholder="Enter your passcode"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && editPasscode) {
                        handleDecryptForEdit()
                      }
                    }}
                  />
                </FormControl>
              </VStack>
            ) : (
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel>Content</FormLabel>
                  <Textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    fontFamily="mono"
                    fontSize="sm"
                    minH="300px"
                    resize="vertical"
                    placeholder="Enter your Markdown content..."
                  />
                </FormControl>
                <Text fontSize="sm" color="stone.500">
                  The content will be re-encrypted with your passcode when saved.
                </Text>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter justifyContent="center">
            <Button variant="ghost" mr={3} onClick={handleCloseEdit}>
              Cancel
            </Button>
            {!contentDecrypted ? (
              <Button
                colorScheme="blue"
                onClick={handleDecryptForEdit}
                isLoading={editState === 'loading-content' || editState === 'decrypting'}
                loadingText={editState === 'loading-content' ? 'Loading...' : 'Decrypting...'}
                isDisabled={!editPasscode}
              >
                Decrypt Content
              </Button>
            ) : (
              <Button
                colorScheme="blue"
                onClick={handleSaveEdit}
                isLoading={editState === 'saving'}
                loadingText="Saving..."
                isDisabled={!editContent.trim()}
              >
                Save Changes
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  )
}
